import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { StepHandle, StepProps } from '../BusinessProfileSetupPage';
import { businessProfileSetupGateway } from '../../services/BusinessProfileSetupGateway';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { identityGateway } from '../../../identity/services';
import { useIdentity } from '../../../identity/state/useIdentity';
import { formatUsPhoneDisplay, usPhoneDigitsFromInput } from '../../../../shared/formatting';
import { validEmail, validPhone } from '../../validation';
import { ApiError } from '../../../identity/types';
import { AdminTabs } from '../../admin-tabs/AdminTabs';
import type { AdminTabItem } from '../../admin-tabs/adminTabsLayout';
import type { MembershipInvitationResponse, MembershipResponse, RoleResponse } from '../../types';

type Tab =
  | { kind: 'member'; membership: MembershipResponse }
  | { kind: 'invitation'; invitation: MembershipInvitationResponse }
  | { kind: 'draft' };

type PendingAction =
  | { kind: 'send-invitation' }
  | { kind: 'revoke-invitation'; invitation: MembershipInvitationResponse }
  | { kind: 'toggle-owner'; membership: MembershipResponse }
  | { kind: 'discard-draft' };

interface DraftForm {
  name: string;
  email: string;
  roleId: string;
  officeNumber: string;
  phone: string;
  owner: boolean;
}

/** EPN-179: "unknown" until the server answers; then whether this environment really delivers e-mail. */
type EmailDelivery = 'unknown' | 'on' | 'off';

const NO_EMAIL_NOTE = 'Email delivery is turned off in this environment, so nothing was emailed. The invitation is only recorded until delivery is turned on.';

const EMPTY_DRAFT: DraftForm = { name: '', email: '', roleId: '', officeNumber: '', phone: '', owner: false };

/** EPN-37 pending-invitation-details correction. `MembershipInvitationResponse` carries `roleId`, not a
 * resolved role name — this looks it up from `roles` (already loaded for the draft form's own Role select),
 * matching MembershipResponse's own `roleName` the way a real member's tab already displays it. Falls back
 * to the raw id only in the defensive case of a role that no longer exists. */
function resolveRoleName(roleId: string, roles: RoleResponse[]): string {
  return roles.find((r) => r.id === roleId)?.name ?? roleId;
}

/** EPN-37 pending-invitation-details correction — UTC-based like AnnouncementsPanel's identical existing
 * pattern elsewhere in this app, since these are absolute server timestamps, not local-timezone events. */
function formatInviteDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export const AdminInfoStep = forwardRef<StepHandle, StepProps>(function AdminInfoStep({ markStepComplete, readOnly }, ref) {
  const { me } = useIdentity();
  const companyId = me?.activeCompanyId ?? null;

  const [members, setMembers] = useState<MembershipResponse[]>([]);
  const [invitations, setInvitations] = useState<MembershipInvitationResponse[]>([]);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  // The unsent draft is purely local/frontend state (no persisted membership or invitation record exists
  // for it) — matching the approved architecture: it never becomes real until Send Invitation succeeds.
  const [draft, setDraft] = useState<DraftForm | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>({ kind: 'draft' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<{ completed: boolean; missingSteps: string[] } | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [emailDelivery, setEmailDelivery] = useState<EmailDelivery>('unknown');

  const load = async () => {
    if (!companyId) return;
    const [memberList, invitationList, roleList] = await Promise.all([
      businessProfileSetupGateway.listMembers(companyId),
      businessProfileSetupGateway.listInvitations(companyId),
      identityGateway.listRoles(),
    ]);
    setMembers(memberList);
    setInvitations(invitationList.filter((i) => i.status === 'Pending'));
    setRoles(roleList);
    // Separate from the lists above: if this one fails the screen still works, it just stays careful about what it claims.
    identityGateway.getEmailDelivery().then((r) => setEmailDelivery(r.enabled ? 'on' : 'off')).catch(() => setEmailDelivery('unknown'));
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Owner designation needs an active Owner; the server enforces the same rule, this only stops offering what would be refused.
  const callerIsOwner = members.some((m) => m.userId === me?.userId && m.isOwner && m.isActive);

  const set = (field: keyof DraftForm, value: string | boolean) => setDraft((prev) => ({ ...(prev ?? EMPTY_DRAFT), [field]: value }));

  const openOrSelectDraft = () => {
    setDraft((prev) => prev ?? EMPTY_DRAFT);
    setActiveTab({ kind: 'draft' });
  };

  const discardDraft = () => {
    setPendingAction(null);
    setDraft(null);
    setErrors({});
    setShowErrors(false);
    const firstMember = members[0];
    const firstInvitation = invitations[0];
    setActiveTab(
      firstMember ? { kind: 'member', membership: firstMember } : firstInvitation ? { kind: 'invitation', invitation: firstInvitation } : { kind: 'draft' },
    );
  };

  const validateDraft = (d: DraftForm): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (d.name.trim().length < 2) errs.name = "Enter the admin's full name (2–100 characters).";
    if (!validEmail(d.email)) errs.email = 'Enter a valid email address, e.g. name@company.com.';
    if (!d.roleId) errs.roleId = 'Select a role.';
    if (!d.officeNumber.trim()) errs.officeNumber = 'Office number is required.';
    else if (!validPhone(d.officeNumber, true)) errs.officeNumber = 'Enter a valid office number, e.g. (555) 555-0100.';
    if (!d.phone.trim()) errs.phone = 'Phone is required.';
    else if (!validPhone(d.phone, true)) errs.phone = 'Enter a valid phone number, e.g. (555) 555-0123.';
    if (d.owner && !callerIsOwner) errs.owner = 'Only an Owner can set another Admin as Owner.';
    const normalizedEmail = d.email.trim().toLowerCase();
    if (
      members.some((m) => m.email.toLowerCase() === normalizedEmail) ||
      invitations.some((i) => i.inviteeEmail.toLowerCase() === normalizedEmail)
    ) {
      errs.email = 'This email is already assigned to another Admin.';
    }
    return errs;
  };

  /** Validates the draft and, only once it's valid, opens the Send Invitation confirmation — the actual
   * createInvitation call happens in performSendInvitation below, only after that confirmation is
   * accepted. */
  const requestSendInvitation = () => {
    if (!draft) return;
    const validationErrors = validateDraft(draft);
    setErrors(validationErrors);
    setShowErrors(true);
    if (Object.keys(validationErrors).length > 0) return;
    setPendingAction({ kind: 'send-invitation' });
  };

  const performSendInvitation = async () => {
    setPendingAction(null);
    if (!companyId || !draft) return;

    setSaving(true);
    setApiError(null);
    try {
      const created = await businessProfileSetupGateway.createInvitation(companyId, {
        email: draft.email.trim().toLowerCase(),
        name: draft.name.trim(),
        roleId: draft.roleId,
        isOwner: draft.owner,
        officeNumber: draft.officeNumber,
        phone: draft.phone,
      });
      setDraft(null);
      setShowErrors(false);
      await load();
      setActiveTab({ kind: 'invitation', invitation: created });
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not send this invitation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const performRevoke = async (invitation: MembershipInvitationResponse) => {
    setPendingAction(null);
    if (!companyId) return;
    setApiError(null);
    try {
      await businessProfileSetupGateway.revokeInvitation(companyId, invitation.id);
      setActiveTab({ kind: 'draft' });
      await load();
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not revoke this invitation.');
    }
  };

  const performToggleOwner = async (membership: MembershipResponse) => {
    setPendingAction(null);
    if (!companyId) return;
    setApiError(null);
    try {
      await businessProfileSetupGateway.setOwnerStatus(companyId, membership.id, !membership.isOwner);
      await load();
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not update Owner status.');
    }
  };

  useImperativeHandle(ref, () => ({
    async saveAndContinue() {
      // The last step — "continue" IS Final Submit. Always server-authoritative: an unsent local draft
      // (if any) is never itself a persisted admin/invitation, so it plays no part in this check — only
      // real memberships/invitations do.
      setApiError(null);
      try {
        const result = await businessProfileSetupGateway.submit();
        setSubmitResult(result);
        if (result.completed) markStepComplete('admin-info');
      } catch (err) {
        setApiError(err instanceof ApiError ? err.message : 'Could not complete Business Profile Setup. Please try again.');
        throw err;
      }
    },
    async saveAndExit() {
      // No persisted draft state exists for Step 6 (see DraftForm's own comment) — nothing to save here.
    },
    async commitEdits() {
      // Nothing to batch-commit: Send/Revoke Invitation and Owner changes each already save immediately
      // through their own dedicated confirmation above — admin-edit's page-level Save Changes has no
      // deferred field state to persist for this section, and MUST NEVER call submit() (Final Submit) the
      // way saveAndContinue does — see StepHandle.commitEdits's own doc comment for why.
    },
    // EPN-37 unsaved-changes correction: Send/Revoke Invitation and Owner toggles each already save
    // immediately through their own dedicated confirmation (see above) — the only local, not-yet-sent state
    // this section can hold is an unsent Admin draft tab, and only once it actually has meaningful input (a
    // freshly opened, still-blank draft isn't yet "unsaved changes"). That same draft's own existing
    // discard-"×" flow (discardDraft, above) is unaffected and still directly available on its tab.
    isDirty: () => draft !== null && JSON.stringify(draft) !== JSON.stringify(EMPTY_DRAFT),
  }));

  const err = (field: string) => (showErrors ? errors[field] : undefined);

  // An unsent draft is local, unsaved input — never shown as "existing" read-only information, and never
  // left selected once the section becomes read-only (e.g. Save Changes closing Edit mode without this
  // step ever remounting — see BusinessProfileSetupPage.tsx's own handleConfirmSaveChanges). Falls back to
  // the first real tab, exactly like discardDraft's own fallback below.
  const effectiveActiveTab: Tab =
    readOnly && activeTab.kind === 'draft'
      ? members[0]
        ? { kind: 'member', membership: members[0] }
        : invitations[0]
          ? { kind: 'invitation', invitation: invitations[0] }
          : { kind: 'draft' }
      : activeTab;

  const pendingActionCopy: { title: string; body: string; confirmLabel: string; destructive: boolean } | null = !pendingAction
    ? null
    : pendingAction.kind === 'send-invitation'
      ? {
          title: emailDelivery === 'off' ? 'Record this invitation?' : 'Send this invitation?',
          body: emailDelivery === 'on'
            ? `An invitation email will be sent to ${draft?.email ?? 'this address'}. They will set their own password and become active once they accept.`
            : emailDelivery === 'off'
              ? `No email will be sent: email delivery is turned off in this environment. The invitation for ${draft?.email ?? 'this address'} will be recorded as pending until delivery is turned on.`
              : `An invitation will be recorded for ${draft?.email ?? 'this address'} and emailed if email delivery is on in this environment. They will set their own password and become active once they accept.`,
          confirmLabel: emailDelivery === 'off' ? 'Record Invitation' : 'Send Invitation',
          destructive: false,
        }
      : pendingAction.kind === 'revoke-invitation'
        ? {
            title: 'Revoke this invitation?',
            body: `The invitation sent to ${pendingAction.invitation.inviteeEmail} will no longer be valid. They will not be able to accept it after this.`,
            confirmLabel: 'Revoke Invitation',
            destructive: true,
          }
        : pendingAction.kind === 'toggle-owner'
          ? pendingAction.membership.isOwner
            ? {
                title: 'Remove Owner status?',
                body: `${pendingAction.membership.email} will no longer have Owner-level access. Owner changes are audited, and at least one active Owner must always remain.`,
                confirmLabel: 'Remove Owner',
                destructive: true,
              }
            : {
                title: 'Grant Owner status?',
                body: `${pendingAction.membership.email} will be granted Owner-level access to this company. Owner changes are audited.`,
                confirmLabel: 'Grant Owner',
                destructive: false,
              }
          : {
              title: 'Discard this new Admin?',
              body: 'This Admin has not been invited yet. The information entered in this tab will be discarded.',
              confirmLabel: 'Discard Admin',
              destructive: true,
            };

  // Tabs name the person: the saved or invited name, then the email; the unsent draft shows what is being typed, live. Keys are record ids.
  const memberLabel = (m: MembershipResponse) => m.displayName?.trim() || m.email;
  const invitationLabel = (i: MembershipInvitationResponse) => i.name?.trim() || i.inviteeEmail;
  const tabItems: AdminTabItem[] = [
    ...members.map((m): AdminTabItem => ({ key: `m:${m.id}`, label: memberLabel(m), kind: 'member' })),
    ...invitations.map((i): AdminTabItem => ({ key: `i:${i.id}`, label: invitationLabel(i), kind: 'pending' })),
    ...(draft && !readOnly ? [{ key: 'draft', label: draft.name.trim() || 'New Admin', kind: 'draft' } as AdminTabItem] : []),
  ];
  const activeKeyOf = (tab: Tab) => (tab.kind === 'member' ? `m:${tab.membership.id}` : tab.kind === 'invitation' ? `i:${tab.invitation.id}` : 'draft');
  const selectTab = (key: string) => {
    if (key === 'draft') return setActiveTab({ kind: 'draft' });
    const member = members.find((m) => `m:${m.id}` === key);
    if (member) return setActiveTab({ kind: 'member', membership: member });
    const invitation = invitations.find((i) => `i:${i.id}` === key);
    if (invitation) setActiveTab({ kind: 'invitation', invitation });
  };

  if (loading) return <p className="helper">Loading…</p>;

  return (
    <div>
      <AdminTabs
        items={tabItems}
        activeKey={activeKeyOf(effectiveActiveTab)}
        onSelect={selectTab}
        onAdd={readOnly ? undefined : openOrSelectDraft}
        onDiscardDraft={readOnly ? undefined : () => setPendingAction({ kind: 'discard-draft' })}
      />

      {effectiveActiveTab.kind === 'member' && (
        <div className="bp-grid">
          <div className="bp-form-group">
            <label>Admin Name</label>
            <div className="bank-derived">{effectiveActiveTab.membership.displayName?.trim() || '—'}</div>
          </div>
          <div className="bp-form-group">
            <label>Email</label>
            <div className="bank-derived">{effectiveActiveTab.membership.email}</div>
          </div>
          <div className="bp-form-group">
            <label>Role</label>
            <div className="bank-derived">{effectiveActiveTab.membership.roleName}</div>
          </div>
          <div className="bp-form-group">
            <label>Status</label>
            <div className="bank-derived">Active</div>
          </div>
          <div className="bp-form-group">
            <label>Office Number</label>
            <div className="bank-derived">{effectiveActiveTab.membership.officeNumber ? formatUsPhoneDisplay(usPhoneDigitsFromInput(effectiveActiveTab.membership.officeNumber)) : '—'}</div>
          </div>
          <div className="bp-form-group">
            <label>Phone</label>
            <div className="bank-derived">{effectiveActiveTab.membership.phone ? formatUsPhoneDisplay(usPhoneDigitsFromInput(effectiveActiveTab.membership.phone)) : '—'}</div>
          </div>
          <div className="bp-form-group span-2">
            <label className="owner-check">
              <input
                type="checkbox"
                checked={effectiveActiveTab.membership.isOwner}
                disabled={readOnly || !callerIsOwner}
                onChange={() => setPendingAction({ kind: 'toggle-owner', membership: effectiveActiveTab.membership })}
              />
              Owner
            </label>
            <div className="helper">
              {callerIsOwner
                ? 'Owner changes are audited. At least one active Owner must remain.'
                : 'Only an Owner can change Owner status. Owner changes are audited and at least one active Owner must remain.'}
            </div>
          </div>
        </div>
      )}

      {effectiveActiveTab.kind === 'invitation' && (
        <div>
          {/* EPN-37 pending-invitation-details correction: shows the actual Admin details persisted with
              the invitation (see resolveRoleName/formatInviteDate above) so a pending invitation reads as a
              pending Admin record, not just an email/status placeholder — every field here is real,
              already-persisted API data (MembershipInvitationResponse), never invented. Office Number/Phone
              only render when the invitation actually has one (optional at invite time — see
              CreateMembershipInvitationRequest). No "invited/sent date" field: MembershipInvitation.CreatedAtUtc
              is persisted server-side but not yet exposed through MembershipInvitationResponse — a
              BACKEND GAP, not a frontend omission; see this feature's own EPN-37 notes rather than
              fabricating a value here. */}
          <div className="bp-grid">
            <div className="bp-form-group">
              <label>Admin Name</label>
              <div className="bank-derived">{effectiveActiveTab.invitation.name || '—'}</div>
            </div>
            <div className="bp-form-group">
              <label>Email</label>
              <div className="bank-derived">{effectiveActiveTab.invitation.inviteeEmail}</div>
            </div>
            <div className="bp-form-group">
              <label>Role</label>
              <div className="bank-derived">{resolveRoleName(effectiveActiveTab.invitation.roleId, roles)}</div>
            </div>
            <div className="bp-form-group">
              <label>Owner</label>
              <div className="bank-derived">{effectiveActiveTab.invitation.isOwner ? 'Yes' : 'No'}</div>
            </div>
            {effectiveActiveTab.invitation.officeNumber && (
              <div className="bp-form-group">
                <label>Office Number</label>
                <div className="bank-derived">{formatUsPhoneDisplay(usPhoneDigitsFromInput(effectiveActiveTab.invitation.officeNumber))}</div>
              </div>
            )}
            {effectiveActiveTab.invitation.phone && (
              <div className="bp-form-group">
                <label>Phone</label>
                <div className="bank-derived">{formatUsPhoneDisplay(usPhoneDigitsFromInput(effectiveActiveTab.invitation.phone))}</div>
              </div>
            )}
            <div className="bp-form-group">
              <label>Status</label>
              <div className="bank-derived">{effectiveActiveTab.invitation.status}</div>
            </div>
            <div className="bp-form-group">
              <label>Expires</label>
              <div className="bank-derived">{formatInviteDate(effectiveActiveTab.invitation.expiresAtUtc)}</div>
            </div>
          </div>

          <div className="admin-invite-card">
            <div className="admin-invite-copy">
              <div className="admin-invite-title">
                Invitation pending <span className="admin-status pending">Pending acceptance</span>
              </div>
              <div className="admin-invite-help">
                {emailDelivery === 'on'
                  ? `An invitation was sent to ${effectiveActiveTab.invitation.inviteeEmail}. They will set their own password and become active after accepting.`
                  : emailDelivery === 'off'
                    ? `An invitation is recorded for ${effectiveActiveTab.invitation.inviteeEmail}. ${NO_EMAIL_NOTE}`
                    : `An invitation is pending for ${effectiveActiveTab.invitation.inviteeEmail}. They will set their own password and become active after accepting.`}
              </div>
            </div>
            {!readOnly && (
              <div className="admin-invite-actions">
                <button
                  type="button"
                  className="bp-btn bp-btn-secondary"
                  onClick={() => setPendingAction({ kind: 'revoke-invitation', invitation: effectiveActiveTab.invitation })}
                >
                  Revoke Invitation
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {effectiveActiveTab.kind === 'draft' && draft && !readOnly && (
        <div>
          <div className="bp-grid">
            <div className="bp-form-group">
              <label htmlFor="bps-adminName">
                Admin Name <span className="req">*</span>
              </label>
              <input
                id="bps-adminName"
                className={`bp-input${err('name') ? ' invalid' : ''}`}
                value={draft?.name ?? ''}
                maxLength={100}
                placeholder="e.g. Jordan Lee"
                onChange={(e) => set('name', e.target.value)}
              />
              {err('name') && <div className="error">{err('name')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-adminEmail">
                Email <span className="req">*</span>
              </label>
              <input
                id="bps-adminEmail"
                className={`bp-input${err('email') ? ' invalid' : ''}`}
                type="email"
                value={draft?.email ?? ''}
                maxLength={254}
                placeholder="e.g. jordan@company.com"
                onChange={(e) => set('email', e.target.value.replace(/\s/g, ''))}
              />
              {err('email') && <div className="error">{err('email')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-adminRole">
                Role <span className="req">*</span>
              </label>
              <select id="bps-adminRole" className={`bp-select${err('roleId') ? ' invalid' : ''}`} value={draft?.roleId ?? ''} onChange={(e) => set('roleId', e.target.value)}>
                <option value="">-- Select --</option>
                {/* SuperAdmin is never assignable through a company's own invitation flow (the server refuses it), so it is not offered. */}
                {roles.filter((r) => r.name !== 'SuperAdmin').map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              {err('roleId') && <div className="error">{err('roleId')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-adminOffice">
                Office Number <span className="req">*</span>
              </label>
              <input
                id="bps-adminOffice"
                className={`bp-input${err('officeNumber') ? ' invalid' : ''}`}
                value={formatUsPhoneDisplay(usPhoneDigitsFromInput(draft?.officeNumber ?? ''))}
                maxLength={22}
                placeholder="(555) 555-0100"
                onChange={(e) => set('officeNumber', usPhoneDigitsFromInput(e.target.value))}
              />
              {err('officeNumber') && <div className="error">{err('officeNumber')}</div>}
            </div>
            <div className="bp-form-group">
              <label htmlFor="bps-adminPhone">
                Phone <span className="req">*</span>
              </label>
              <input
                id="bps-adminPhone"
                className={`bp-input${err('phone') ? ' invalid' : ''}`}
                value={formatUsPhoneDisplay(usPhoneDigitsFromInput(draft?.phone ?? ''))}
                maxLength={22}
                placeholder="(555) 555-0123"
                onChange={(e) => set('phone', usPhoneDigitsFromInput(e.target.value))}
              />
              {err('phone') && <div className="error">{err('phone')}</div>}
            </div>
            <div className="bp-form-group span-2">
              <label className="owner-check">
                <input type="checkbox" checked={draft?.owner ?? false} disabled={!callerIsOwner} aria-describedby="bps-owner-help" onChange={(e) => set('owner', e.target.checked)} />
                Set as <strong>Owner</strong>
              </label>
              <div className="helper" id="bps-owner-help">
                {callerIsOwner
                  ? 'Gives this person Owner-level access once they accept the invitation. Owner changes are audited and at least one active Owner must remain.'
                  : 'Only an Owner can set another Admin as Owner. Owner changes are audited and at least one active Owner must remain.'}
              </div>
              {err('owner') && <div className="error">{err('owner')}</div>}
            </div>
          </div>

          <div className="admin-invite-card">
            <div className="admin-invite-copy">
              <div className="admin-invite-title">
                New Admin <span className="admin-status draft">Not invited</span>
              </div>
              <div className="admin-invite-help">
                {emailDelivery === 'off'
                  ? `Enter the admin's name and email, then record the invitation. Email delivery is turned off in this environment, so nothing is emailed.`
                  : `Enter the admin's name and email, then send the invitation. The invitation is sent only when you click Send Invitation.`}
              </div>
            </div>
            <div className="admin-invite-actions">
              <button type="button" className="bp-btn bp-btn-secondary" onClick={() => setPendingAction({ kind: 'discard-draft' })}>
                <i className="fa fa-trash-o bps-icon" aria-hidden="true" />
                Discard Draft
              </button>
              <button type="button" className="bp-btn bp-btn-primary" onClick={requestSendInvitation} disabled={saving}>
                <i className="fa fa-envelope bps-icon" aria-hidden="true" />
                {emailDelivery === 'off' ? 'Record Invitation' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {apiError && <div className="notice" style={{ color: 'var(--bps-danger)' }}>{apiError}</div>}

      {submitResult && (
        <div className="notice" style={{ marginTop: 16 }}>
          {submitResult.completed ? (
            'Business Profile Setup completed successfully.'
          ) : (
            <>
              {submitResult.missingSteps.includes('Admin Information') && (
                <div className="bp-banner-error" role="alert" data-testid="owner-required-banner">
                  At least one administrator must be designated as Owner.
                </div>
              )}
              The following steps still need attention before setup can be completed:
              <ul>
                {submitResult.missingSteps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {pendingAction && pendingActionCopy && (
        <ConfirmDialog
          open
          title={pendingActionCopy.title}
          body={pendingActionCopy.body}
          confirmLabel={pendingActionCopy.confirmLabel}
          cancelLabel={pendingAction.kind === 'discard-draft' ? 'Keep Editing' : 'Cancel'}
          destructive={pendingActionCopy.destructive}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => {
            if (pendingAction.kind === 'send-invitation') void performSendInvitation();
            else if (pendingAction.kind === 'revoke-invitation') void performRevoke(pendingAction.invitation);
            else if (pendingAction.kind === 'toggle-owner') void performToggleOwner(pendingAction.membership);
            else discardDraft();
          }}
        />
      )}
    </div>
  );
});
